import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { Typography, makeStyles } from '@material-ui/core'
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat'

import { SECONDARY } from 'app/constants/button'

import ContainedButton from 'app/componentV4/Button/ContainedButton'
import { ReactComponent as Subscriber } from 'app/components/icons/reading-a-book.svg'
import { ReactComponent as MeetAbby } from 'app/components/icons/listening-to-feedback.svg'
import { ReactComponent as ComposeMessage } from 'app/components/icons/team-brainstorming.svg'

const useStyles = makeStyles((theme) => ({
  container: {
    height: '600px',
    maxHeight: '90vh',
    [theme.breakpoints.up(1000)]: {
      width: '900px',
      maxWidth: '95vw',
    },
  },
  banner: {
    padding: '3rem',
    [theme.breakpoints.down(1000)]: {
      display: 'none',
    },
    [theme.breakpoints.up(1000)]: {
      width: '50%',
      display: 'block',
      backgroundSize: 'cover !important',
      backgroundPosition: 'center !important',
    },
  },
  content: {
    backgroundColor: 'white',
    overflow: 'auto',
    [theme.breakpoints.down(1000)]: {
      margin: 'auto',
      padding: '3rem',
      borderRadius: '2.4rem',
      width: 'calc(100vw - 50px)',
      height: '100%',
      '& .responsive-vdo': {
        display: 'block',
      },
    },
    [theme.breakpoints.up(1000)]: {
      width: '50%',
      padding: '3rem',
      borderTopLeftRadius: '2.4rem',
      borderBottomLeftRadius: '2.4rem',
      '& .responsive-vdo': {
        display: 'none',
      },
    },
  },
}))

const features = [
  {
    id: 'COMPOSE_MESSAGE',
    titleKey: 'feature.highlight.compose.message',
    descKey: 'feature.highlight.compose.message.detail',
    bg: 'bg-sky-50',
    border: 'border-sky-500',
    icon: <ComposeMessage />,
    src: process.env.REACT_APP_TUTORIAL_VIDEO_PRESENT_COMPOSE_MESSAGE,
  },
  {
    id: 'ABBY',
    titleKey: 'feature.highlight.abby',
    descKey: 'feature.highlight.abby.detail',
    bg: 'bg-green-50',
    border: 'border-green-500',
    icon: <MeetAbby />,
    src: process.env.REACT_APP_TUTORIAL_VIDEO_PRESENT_ABBY,
  },
  {
    id: 'CRM',
    titleKey: 'feature.highlight.crm',
    descKey: 'feature.highlight.crm.detail',
    bg: 'bg-orange-50',
    border: 'border-orange-500',
    icon: <Subscriber />,
    src: process.env.REACT_APP_TUTORIAL_VIDEO_PRESENT_CRM,
  },
]

const FeatureHighlights = ({ onClose, isOnboard = false }) => {
  const videoRef = useRef()
  const classes = useStyles()
  const [active, setActive] = useState(features[0])
  const { formatMessage } = useIntl()

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load()
    }
  }, [active.src])

  return (
    <div className={classes.container}>
      <Typography className="font-500 text-16 text-primary m-10">
        <FormattedMessage id="feature.highlight.header" />
      </Typography>
      <div className="flex h-[calc(100%-163px)]">
        <div className={clsx(classes.content)}>
          <div className="flex flex-col justify-between h-full space-y-10">
            {features.map((feat) => {
              const isActive = feat.id === active.id
              return (
                <>
                  <button
                    key={feat.id}
                    onClick={() => setActive(feat)}
                    className={`
                  flex items-start space-x-4 p-5 rounded-lg 
                  ${feat.bg} 
                  ${isActive ? `border-1 ${feat.border}` : 'border border-transparent'} 
                  hover:border-blue-300
                  focus:outline-none
                `}
                  >
                    <div className="mt-1">{feat.icon}</div>
                    <div className="text-left">
                      <div className="flex justify-between">
                        <Typography className="font-medium text-14 text-primary">
                          {formatMessage({ id: feat.titleKey })}
                        </Typography>
                        {isActive && <TrendingFlatIcon fontSize="small" />}
                      </div>
                      <Typography className="text-12 text-primary-300">
                        {formatMessage({ id: feat.descKey })}
                      </Typography>
                    </div>
                  </button>
                  {isActive && (
                    <div className="responsive-vdo ">
                      <div className="flex items-center h-full">
                        <video width="100%" controls autoPlay>
                          <source src={feat.src} type="video/mp4" />
                        </video>
                      </div>
                    </div>
                  )}
                </>
              )
            })}
          </div>
        </div>
        <div className={classes.banner}>
          <div className="flex items-center h-full">
            <video ref={videoRef} width="100%" controls autoPlay>
              <source src={active.src} type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
      <div className="w-full flex justify-center my-10">
        <ContainedButton
          type="submit"
          color={SECONDARY}
          onClick={() => onClose()}
        >
          {isOnboard ? (
            <FormattedMessage
              id="step3.Get Started"
              defaultMessage="Get Started"
            />
          ) : (
            <FormattedMessage id="text.close" />
          )}
        </ContainedButton>
      </div>
    </div>
  )
}
FeatureHighlights.propTypes = {
  onClose: PropTypes.func,
  isOnboard: PropTypes.bool,
}

export default FeatureHighlights
